import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * NITEWIRE Community Rules modal — surfaced from the auth disclaimer link.
 * Kept intentionally readable on both mobile and desktop.
 */
export default function CommunityRulesModal({ open, onOpenChange }) {
  const rules = [
    {
      title: "18+ ONLY",
      body: "NITEWIRE is an adults-only space. You must be 18 years of age or older to enter. If you're under 18, please leave.",
    },
    {
      title: "BE HUMAN",
      body: "No hate speech, harassment, threats, stalking, doxxing, or targeted abuse. Disagree hard — but attack ideas, not people.",
    },
    {
      title: "NO ILLEGAL CONTENT",
      body: "Zero tolerance for CSAM, non-consensual imagery, incitement to violence, or anything unlawful. Offenders are permanently banned and reported.",
    },
    {
      title: "CONSENT & PRIVACY",
      body: "Don't share other people's private info, photos, or DMs without their permission. Sexual content between consenting adults belongs in the rooms marked for it.",
    },
    {
      title: "NO SPAM OR SCAMS",
      body: "No mass promotion, crypto pump-and-dumps, phishing links, romance scams, or link farms. Genuine conversation > selling.",
    },
    {
      title: "STAY ON TOPIC (MOSTLY)",
      body: "Each room has a vibe. Respect it. If you want a new topic, start a new room instead of derailing an existing one.",
    },
    {
      title: "REPORT, DON'T RETALIATE",
      body: "Every room has a report button. Use it. Mods and admins review flagged messages and IPs. Vigilante behavior can also get you banned.",
    },
    {
      title: "YOUR ACCOUNT IS YOURS",
      body: "Don't share logins, impersonate real people, or run bots. One human, one handle.",
    },
    {
      title: "WE MAY MODERATE",
      body: "Admins can edit, delete, mute, ban, or IP-block anyone who breaks these rules. We keep logs of session IPs for safety. Access is deleted on request unless required for a live investigation.",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="community-rules-modal"
        className="max-w-2xl max-h-[85vh] overflow-y-auto bg-obsidian text-paper border border-acid/40 rounded-none p-0"
      >
        <DialogHeader className="p-6 border-b border-white/10 sticky top-0 bg-obsidian z-10">
          <div className="font-mono text-acid text-[10px] tracking-widest mb-2">
            // NW.COMMUNITY_RULES v1.0
          </div>
          <DialogTitle className="font-heading text-3xl md:text-4xl leading-none text-paper">
            HOUSE <span className="text-acid">RULES.</span>
          </DialogTitle>
          <DialogDescription className="font-mono text-white/60 text-xs md:text-sm mt-3">
            Short version: be an adult, be kind, don't do crimes. Long version below.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-5">
          {rules.map((r, i) => (
            <div
              key={r.title}
              className="border-l-2 border-acid/60 pl-4"
              data-testid={`rule-${i + 1}`}
            >
              <div className="font-mono text-acid text-[11px] tracking-widest mb-1">
                {String(i + 1).padStart(2, "0")} — {r.title}
              </div>
              <p className="font-body text-white/85 text-sm md:text-base leading-relaxed">
                {r.body}
              </p>
            </div>
          ))}

          <div className="pt-4 mt-2 border-t border-white/10 font-mono text-[10px] tracking-widest text-white/40">
            LAST UPDATED · NW//2026 · QUESTIONS? HIT THE REPORT BUTTON IN ANY ROOM.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
